/**
 * @file ConfirmSavedCard.tsx
 * @brief "Confirm with your bank" on a saved card: the hook that runs the
 *        bank check, the line under a card row, and the banner a confirm
 *        email's link opens. Shared by personal Billing → Cards and Company
 *        Admin → Billing (web and the Mac app).
 *
 * @details
 * The phone is payment-free (ADR-043 update): a phone hold that needs 3DS is
 * cancelled and an email sends the person here. The check is a SetupIntent
 * for the card already saved (BACKEND f5e603e: `POST /billing/setup-intent`
 * with `{ paymentMethodId }`, usage off_session), confirmed in the page with
 * `stripe.confirmCardSetup`, whose 3DS window is a Stripe frame and so also
 * works inside the Mac app. Done: "Done. You can start again on your phone."
 *
 * Honest limit (plan, 20-10-2026): the bank decides; a later phone hold can
 * still be asked for 3DS, and the email then comes again.
 *
 * @references
 * - confirmCard.ts (link params, which card)
 * - scratch/plan-confirm-saved-card-2026-10-20.md
 *
 * @author DES
 * @date 21-10-2026
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, LoaderCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ApiClientError } from "@/shared/api/client";
import { getStripe } from "@/shared/stripe/stripeClient";
import { cn } from "@/shared/theme/cn";
import { pickCardToConfirm, readConfirmParams, withoutConfirmParams } from "./confirmCard";

export const CONFIRM_DONE = "Done. You can start again on your phone.";
const BANK_SAID_NO = "Your bank didn't confirm the card. Try again or use another card.";

export type CardConfirmState =
  | { status: "running" }
  | { status: "done" }
  | { status: "failed"; message: string };

/**
 * Runs the bank check for one saved card at a time and keeps each card's
 * outcome for its row and the banner.
 * @param createIntent Asks the server for a SetupIntent on that card.
 * @param onConfirmed After a card is confirmed (refresh the list).
 */
export function useConfirmSavedCard({
  createIntent,
  onConfirmed,
}: {
  createIntent: (paymentMethodId: string) => Promise<string>;
  onConfirmed?: () => void;
}) {
  const [states, setStates] = useState<Record<string, CardConfirmState>>({});
  const set = (id: string, s: CardConfirmState) => setStates((cur) => ({ ...cur, [id]: s }));

  const confirm = useCallback(
    async (paymentMethodId: string) => {
      set(paymentMethodId, { status: "running" });
      try {
        const stripe = await getStripe();
        if (!stripe) {
          set(paymentMethodId, { status: "failed", message: "Card payments aren't set up on this site." });
          return;
        }
        const clientSecret = await createIntent(paymentMethodId);
        // The card is already on the intent: Stripe opens the bank's window.
        const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret);
        if (error || setupIntent?.status !== "succeeded") {
          set(paymentMethodId, { status: "failed", message: BANK_SAID_NO });
          return;
        }
        set(paymentMethodId, { status: "done" });
        onConfirmed?.();
      } catch (err) {
        set(paymentMethodId, {
          status: "failed",
          message:
            err instanceof ApiClientError && err.code === "CARD_NOT_FOUND"
              ? "That card isn't on this account any more. Add it again."
              : err instanceof ApiClientError
                ? err.message
                : "Couldn't reach your bank. Please try again.",
        });
      }
    },
    [createIntent, onConfirmed],
  );

  return { states, confirm, busy: Object.values(states).some((s) => s.status === "running") };
}

/** The line under a card row: checking, done, or what went wrong. */
export function CardConfirmStatus({ state }: { state: CardConfirmState | undefined }) {
  if (!state) return null;
  return (
    <p
      role="status"
      className={cn(
        "mt-0.5 flex items-center gap-1.5 text-xs",
        state.status === "done" ? "text-success" : state.status === "failed" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {state.status === "running" ? (
        <>
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> Checking with your bank…
        </>
      ) : state.status === "done" ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5" /> {CONFIRM_DONE}
        </>
      ) : (
        state.message
      )}
    </p>
  );
}

/** The menu item's face. */
export function ConfirmWithBankLabel() {
  return (
    <>
      <ShieldCheck className="h-4 w-4" /> Confirm with your bank
    </>
  );
}

type BannerCard = { id: string; brand: string; last4: string };

/**
 * At the top of the cards page when the person came from a confirm email
 * (`?confirm=1[&card=pm_…]`): "Your bank asked to confirm your card", the
 * card, and the button. After success it says so, and the link's params come
 * off the URL so a reload doesn't show it again.
 */
export function ConfirmCardBanner({
  cards,
  defaultId,
  brandLabel,
  states,
  onConfirm,
  onAddCard,
}: {
  cards: readonly BannerCard[];
  defaultId: string | null;
  brandLabel: (brand: string) => string;
  states: Record<string, CardConfirmState>;
  onConfirm: (paymentMethodId: string) => void;
  onAddCard: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { confirm, cardId } = readConfirmParams(location.search);
  const card = confirm ? pickCardToConfirm(cards, { cardId, defaultId }) : null;
  const state = card ? states[card.id] : undefined;
  const done = state?.status === "done";
  // Once confirmed, the link has done its job.
  const [shownDone, setShownDone] = useState(false);
  // A new confirm link while the page is open starts over.
  useEffect(() => {
    if (confirm && !done) setShownDone(false);
  }, [confirm, done]);
  useEffect(() => {
    if (!done || shownDone) return;
    setShownDone(true);
    navigate({ pathname: location.pathname, search: withoutConfirmParams(location.search) }, { replace: true });
  }, [done, shownDone, navigate, location.pathname, location.search]);

  if (!confirm && !shownDone) return null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-4 py-3.5 md:px-5",
        shownDone ? "bg-success/10" : "bg-brand/[0.06]",
      )}
    >
      {shownDone ? (
        <p className="flex items-center gap-2 text-sm font-medium text-success">
          <CheckCircle2 className="h-4 w-4" /> {CONFIRM_DONE}
        </p>
      ) : (
        <>
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold">
              <ShieldCheck className="h-4 w-4 text-brand" /> Your bank asked to confirm your card
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {card
                ? `${brandLabel(card.brand)} •••• ${card.last4}. It takes a moment in your bank's own window.`
                : "There's no card saved yet. Add one to continue."}
            </p>
            {state?.status === "failed" ? (
              <p className="mt-1 text-xs text-destructive">{state.message}</p>
            ) : null}
          </div>
          {card ? (
            <Button size="sm" disabled={state?.status === "running"} onClick={() => onConfirm(card.id)}>
              {state?.status === "running" ? (
                <LoaderCircle className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="mr-1.5 h-4 w-4" />
              )}
              Confirm with your bank
            </Button>
          ) : (
            <Button size="sm" onClick={onAddCard}>
              Add card
            </Button>
          )}
        </>
      )}
    </div>
  );
}
